import { useTranslation } from "react-i18next";
import { Shield, Lock, Zap, Award, CheckCircle, Building2 } from "lucide-react";
import { motion, useScroll, useTransform, useInView, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState, useMemo } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import logoItacWhiteFull from "@/assets/logo-itac-white-full.webp";

/* ── Animated particle field (idle-started, throttled, pauses off-screen) ── */
function ParticleField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const coarse = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (coarse || reduced) return;
    const start = () => setEnabled(true);
    const w = window as any;
    const useIdle = typeof w.requestIdleCallback === "function";
    const id = useIdle ? w.requestIdleCallback(start, { timeout: 3000 }) : window.setTimeout(start, 1500);
    return () => {
      if (useIdle && typeof w.cancelIdleCallback === "function") w.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    let animId = 0;
    let running = true;
    let lastFrame = 0;
    const FRAME_MS = 1000 / 24;

    const resize = () => {
      canvas.width = Math.min(window.innerWidth, 1600);
      canvas.height = Math.min(window.innerHeight, 1200);
    };
    resize();
    let resizeTimer: number;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(resize, 200);
    };
    window.addEventListener("resize", onResize, { passive: true });

    const count = window.innerWidth < 1024 ? 24 : 40;
    const LINK = 110;
    const particles = Array.from({ length: count }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      size: Math.random() * 2 + 0.5,
      opacity: Math.random() * 0.3 + 0.1,
    }));

    const draw = (now: number) => {
      animId = requestAnimationFrame(draw);
      if (!running || now - lastFrame < FRAME_MS) return;
      lastFrame = now;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 98, 152, ${p.opacity})`;
        ctx.fill();
      }

      ctx.lineWidth = 0.5;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          if (dx > LINK || dx < -LINK) continue;
          const dy = particles[i].y - particles[j].y;
          if (dy > LINK || dy < -LINK) continue;
          const dist = Math.hypot(dx, dy);
          if (dist < LINK) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0, 98, 152, ${0.06 * (1 - dist / LINK)})`;
            ctx.stroke();
          }
        }
      }
    };
    animId = requestAnimationFrame(draw);

    const observer = new IntersectionObserver(([e]) => (running = e.isIntersecting), { threshold: 0 });
    observer.observe(canvas);
    const onVisibility = () => (running = !document.hidden);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelAnimationFrame(animId);
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("resize", onResize);
      clearTimeout(resizeTimer);
    };
  }, [enabled]);

  if (!enabled) return null;
  return <canvas ref={canvasRef} aria-hidden="true" className="absolute inset-0 w-full h-full" />;
}

/* ── Floating orbs ── */
function FloatingOrbs() {
  const [light, setLight] = useState(true);

  useEffect(() => {
    const coarse = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (coarse || reduced) return;
    const w = window as any;
    const start = () => setLight(false);
    const useIdle = typeof w.requestIdleCallback === "function";
    const id = useIdle ? w.requestIdleCallback(start, { timeout: 3000 }) : window.setTimeout(start, 1500);
    return () => {
      if (useIdle && typeof w.cancelIdleCallback === "function") w.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, []);

  const orbs = useMemo(
    () => [
      { size: 600, x: "10%", y: "15%", color: "201,100%,30%", delay: 0 },
      { size: 500, x: "70%", y: "55%", color: "83,87%,35%", delay: 2 },
      { size: 400, x: "45%", y: "75%", color: "19,100%,50%", delay: 4 },
      { size: 450, x: "85%", y: "10%", color: "201,80%,45%", delay: 1 },
    ],
    [],
  );

  if (light) {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {orbs.slice(0, 2).map((orb, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: orb.size,
              height: orb.size,
              left: orb.x,
              top: orb.y,
              background: `radial-gradient(circle, hsla(${orb.color}, 0.12) 0%, transparent 70%)`,
              filter: "blur(60px)",
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {orbs.map((orb, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            width: orb.size,
            height: orb.size,
            left: orb.x,
            top: orb.y,
            background: `radial-gradient(circle, hsla(${orb.color}, 0.12) 0%, transparent 70%)`,
            filter: "blur(100px)",
          }}
          animate={{
            x: [0, 40, -30, 0],
            y: [0, -50, 30, 0],
            scale: [1, 1.15, 0.9, 1],
          }}
          transition={{
            duration: 14 + i * 3,
            repeat: Infinity,
            delay: orb.delay,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}

/* ── Rotating words ── */
function RotatingWords({ words }: { words: string[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setIndex((i) => (i + 1) % words.length), 3000);
    return () => clearInterval(interval);
  }, [words.length]);

  return (
    <span
      className="inline-flex relative overflow-hidden items-center justify-center"
      style={{ height: "1.8em", minWidth: "14ch" }}
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={words[index]}
          initial={{ y: "100%", opacity: 0, filter: "blur(8px)" }}
          animate={{ y: "0%", opacity: 1, filter: "blur(0px)" }}
          exit={{ y: "-100%", opacity: 0, filter: "blur(8px)" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute text-white whitespace-nowrap font-bold"
          style={{ fontFamily: "'Syne', system-ui, sans-serif", letterSpacing: "-0.02em" }}
        >
          {words[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

/* ── Counter ── */
function Counter({ target, suffix = "" }: { target: string; suffix?: string }) {
  const num = parseInt(target.replace(/[^0-9]/g, ""));
  const hasPlus = target.includes("+");
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 2200;
          const start = performance.now();
          const animate = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 5);
            setCount(Math.floor(eased * num));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [num]);

  return (
    <div ref={ref} className="text-3xl lg:text-4xl font-bold text-white tabular-nums">
      {count.toLocaleString()}
      {hasPlus ? "+" : ""}
      {suffix}
    </div>
  );
}

export function HeroSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const yContent = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacityContent = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scaleContent = useTransform(scrollYProgress, [0, 0.5], [1, 0.92]);

  const rotatingWords = [t("hero.security"), t("hero.automation"), t("hero.governance")];

  const statTooltips = [
    t("hero.stat1Tooltip"),
    t("hero.stat2Tooltip"),
    t("hero.stat3Tooltip"),
    t("hero.stat4Tooltip"),
  ];

  const stats = [
    { value: "20", label: t("hero.stat1Label"), suffix: "+", icon: <Award className="w-5 h-5" /> },
    { value: "82", label: t("hero.stat2Label"), suffix: "+", icon: <Building2 className="w-5 h-5" /> },
    { value: "100", label: t("hero.stat3Label"), suffix: "%", icon: <CheckCircle className="w-5 h-5" /> },
    { value: "", label: t("hero.stat4Label"), sublabel: t("hero.stat4Sublabel"), isText: true, icon: <Shield className="w-5 h-5" /> },
  ];

  return (
    <section ref={sectionRef} className="hero-locked-dark relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Layered backgrounds */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(160deg, hsl(207, 90%, 8%) 0%, hsl(207, 60%, 14%) 40%, hsl(201, 50%, 18%) 70%, hsl(207, 90%, 10%) 100%)",
        }}
      />
      <ParticleField />
      <FloatingOrbs />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[hsl(207,90%,8%)] z-[2]" />

      {/* Logo background - full width */}
      <motion.div
        initial={{ opacity: 0, scale: 1.1 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
        className="absolute inset-0 z-[1] flex items-center justify-center"
      >
        <img loading="eager" fetchPriority="high" decoding="async" width={1000} height={543}
          src={logoItacWhiteFull}
          alt=""
          className="w-[85%] max-w-5xl h-auto object-contain opacity-[0.12]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(207,90%,8%)]/30 via-transparent to-[hsl(207,90%,8%)]" />
      </motion.div>

      {/* Main content */}
      <motion.div
        style={{ y: yContent, opacity: opacityContent, scale: scaleContent }}
        className="container mx-auto px-4 lg:px-8 pt-28 pb-16 relative z-10"
      >
        <div className="max-w-6xl mx-auto">

          {/* Divider line */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="h-px w-48 mx-auto mb-10 bg-gradient-to-r from-transparent via-white/30 to-transparent"
          />

          {/* Headline */}
          <div className="text-center mb-6">
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="text-3xl sm:text-4xl lg:text-[3.5rem] font-bold leading-tight text-white"
            >
              {t("hero.title")}{" "}
              <span className="sr-only">{rotatingWords.join(", ")}</span>
              <motion.span
                aria-hidden="true"
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="mt-4 h-[1.5em] text-3xl sm:text-4xl lg:text-[3.5rem] font-bold leading-tight flex items-center justify-center"
              >
                <RotatingWords words={rotatingWords} />
              </motion.span>
            </motion.h1>
          </div>


          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1, delay: 1, ease: [0.22, 1, 0.36, 1] }}
            className="text-center text-base lg:text-lg text-white/50 max-w-2xl mx-auto mb-14 leading-relaxed"
          >
            {t("hero.subtitle")}
          </motion.p>

          {/* Spacer */}
          <div className="mb-20" />
        </div>

        {/* Stats */}
        <TooltipProvider delayDuration={200}>
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5">
            {stats.map((stat, i) => (
              <Tooltip key={i}>
                <TooltipTrigger asChild>
                <motion.div
                  initial={{ opacity: 0, y: 40, scale: 0.85 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.7, delay: 1.5 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -6, scale: 1.03 }}
                  className="group relative text-center p-5 lg:p-7 rounded-2xl bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] hover:border-white/20 transition-all duration-500 overflow-hidden cursor-pointer"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[hsl(207,78%,40%)]/10 via-transparent to-[hsl(27,86%,53%)]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
                  <div className="relative">
                    <div className="flex justify-center mb-2 text-[hsl(201,100%,50%)]">{stat.icon}</div>
                    {(stat as any).isText ? (
                      <>
                        <div className="text-2xl lg:text-3xl font-bold text-white tabular-nums">{stat.label}</div>
                        <div className="text-sm text-white/40 mt-2 font-medium">{(stat as any).sublabel}</div>
                      </>
                    ) : (
                      <>
                        <Counter target={stat.value} suffix={stat.suffix} />
                        <div className="text-sm text-white/40 mt-2 font-medium">{stat.label}</div>
                      </>
                    )}
                  </div>
                </motion.div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-center px-4 py-3" style={{ backgroundColor: "hsl(207, 90%, 12%)", color: "white", borderColor: "rgba(255,255,255,0.1)" }}>
                  <p className="text-sm">{statTooltips[i]}</p>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </div>
        </TooltipProvider>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5, duration: 1 }}
        style={{ opacity: opacityContent }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="flex flex-col items-center gap-2"
        >
          <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-white/25">Scroll</span>
          <div className="w-5 h-8 rounded-full border border-white/15 flex items-start justify-center pt-1.5">
            <motion.div
              animate={{ y: [0, 8, 0], opacity: [0.6, 0.15, 0.6] }}
              transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
              className="w-1 h-1.5 rounded-full bg-white/40"
            />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
